import { effect } from "@preact/signals";
import * as preact from "preact";
import Sortable from "sortablejs";

import * as deviceDetect from "deviceDetect";
import * as genericModals from "genericModals";
import SelectionActionList, { SelectionAction } from "SelectionActionList";
import * as bookUtils from "shared/bookUtils";
import * as languageMap from "shared/languageMap";
import { userProfilePath } from "shared/usernameValidation";
import * as utils from "shared/utils";
import BookCollectionStore from "stores/BookCollectionStore";
import BookStore from "stores/BookStore";
import { getBookIndex, getValidBooks } from "stores/ShelvesStore";
import stores from "stores/stores";
import Button from "ui/Button";
import { completedCheckIcon } from "views/BookCoverView";
import { formatLastRead, sortBooks, SortColumn } from "views/librarySort";
import SelectAllView from "views/SelectAllView";
import SelectionActionView from "views/SelectionActionView";
import { showShelvesSheet } from "views/ShelvesSheet";
import SortAndFilterMenu from "views/SortAndFilterMenu";

interface PrivateLibraryListProps {
  books: BookCollectionStore;
  tag?: string;
  shelfID?: string | null;
  /** Browsing and searching the texts, in the same row as the actions */
  searchControls: preact.ComponentChildren;
  /** Ending the row (atop it on phones) */
  addButtons?: preact.ComponentChildren;
}

const bookContentType = (bookJSON: {
  youTubeID?: string;
  audioFileID?: string;
}): "video" | "audio" | "text" => {
  if (bookJSON.youTubeID) return "video";
  if (bookJSON.audioFileID) return "audio";
  return "text";
};

const contentTypeLabel = (type: "video" | "audio" | "text") => {
  if (type === "video") return "Video";
  if (type === "audio") return "Audio";
  return "Text";
};

const contentTypeIcon = (type: "video" | "audio" | "text") => {
  if (type === "video") return "icon-film";
  if (type === "audio") return "icon-headphones";
  return "icon-file-text";
};

interface PrivateLibraryListState {
  previousCheckboxChecked?: boolean;
  previousCheckboxID?: string;
  // Session-only sort: null = default order (shelf order, or most-recently-read
  // for non-shelf lists). Set by clicking a column header.
  sortColumn: SortColumn | null;
  sortAscending: boolean;
}

const dateFromObjectId = (objectId: string) =>
  new Date(parseInt(objectId.substring(0, 8), 16) * 1000).toLocaleDateString();

const containsTag = (book: BookStore, tagName?: string) => {
  if (!tagName) return true;
  const tags = book.book.value.userBook?.tags ?? [];
  const visibleLibraryTags = ["queue", "completed", "abandoned", "shared"];
  if (tagName === "deleted") {
    return !visibleLibraryTags.some((tag) => tags.includes(tag));
  }
  if (tagName === "all") {
    return visibleLibraryTags.some((tag) => tags.includes(tag));
  }
  return book.hasTag(tagName);
};

interface ReadStatus {
  label: string;
  /** How far into the text, where that's shown as a bar */
  percent?: number;
  completed?: boolean;
}

const readStatus = (bookJSON: any): ReadStatus | null => {
  const tags = bookJSON.userBook?.tags ?? [];
  if (tags.includes("completed")) {
    return { label: "Completed", percent: 100, completed: true };
  }
  if (tags.includes("abandoned")) {
    return { label: "Archived" };
  }
  if (tags.includes("queue")) {
    return bookJSON.readTo
      ? { label: `Read ${bookJSON.readTo}%`, percent: bookJSON.readTo }
      : { label: "To Read" };
  }
  return null;
};

// Progress as a bar in the text's category colour, with a check once completed
// (as on Public Texts' covers), its label in the tooltip; other statuses as a
// short, muted word, sitting back on the row rather than competing with the
// title.
const renderReadStatus = ({ label, percent, completed }: ReadStatus) =>
  percent === undefined ? (
    <span class="readStatusLabel muted">{label}</span>
  ) : (
    <span class="readingProgress" title={label}>
      <progress max={100} value={percent} aria-label={label}>
        {label}
      </progress>
      {completed ? (
        <span class="completedCheck">{completedCheckIcon()}</span>
      ) : null}
    </span>
  );

/** The category of a text that has one, by the name users see ("Dialogue") */
const categoryOf = (bookJSON: any): string | null =>
  bookUtils.categories.includes(bookJSON.category) &&
  bookJSON.category !== "Other"
    ? bookUtils.categoryLabel(bookJSON.category)
    : null;

const renderCategoryChip = (category: string | null) =>
  category ? <span class="categoryChip">{category}</span> : null;

const permissionTitle = (bookJSON: any) => {
  if (!bookJSON.public) return "Private";
  return bookJSON.hide ? "Anyone with the link can access" : "Public";
};

const permissionIcon = (bookJSON: any) => {
  if (!bookJSON.public) return "icon-lock";
  return bookJSON.hide ? "icon-unlock" : "icon-globe";
};

// The mobile sort menu replaces the column headers hidden by the mobile
// layout, so it offers the same columns under the same names.
const sortOptions: { value: SortColumn; label: string }[] = [
  { value: "title", label: "Title" },
  { value: "contentType", label: "Type" },
  { value: "difficulty", label: "Difficulty" },
  { value: "permissions", label: "Sharing" },
  { value: "owner", label: "Owner" },
  { value: "length", label: "Length" },
  { value: "readStatus", label: "Progress" },
  { value: "lastRead", label: "Last Read" },
];

export class PrivateLibraryList extends preact.Component<
  PrivateLibraryListProps,
  PrivateLibraryListState
> {
  private disposeBooksEffect?: () => void;
  private sortable?: Sortable;
  private tbodyElement?: HTMLTableSectionElement | null;
  private checkboxClickShiftKey = false;

  state: PrivateLibraryListState = { sortColumn: null, sortAscending: true };

  componentDidMount() {
    this.applyVisibilityFilter();
    this.disposeBooksEffect = effect(() => {
      for (const book of this.props.books.books.value) {
        void book.book.value;
      }
      void this.props.books.selectionStore.itemIDs.value;
      void this.props.books.selectionStore.wholePage.value;
      void this.props.books.selectionStore.wholeCollection.value;
      void deviceDetect.smallViewport.value;
      this.applyVisibilityFilter();
      this.forceUpdate();
    });
    this.setupSortable();
  }

  componentDidUpdate() {
    this.applyVisibilityFilter();
    this.setupSortable();
  }

  componentWillUnmount() {
    this.props.books.setVisibilityFilter(null);
    this.disposeBooksEffect?.();
    this.sortable?.destroy();
  }

  applyVisibilityFilter() {
    this.props.books.setVisibilityFilter((book) => this.shouldShowBook(book));
  }

  createSelectionActionList() {
    return new SelectionActionList(
      [
        {
          id: "restoreToReadingQueue",
          name: "Restore to Reading Queue",
          applyQuery: { "userBook.tags": { $size: 0 } },
        },
        {
          id: "restoreToCompleted",
          name: "Restore to Completed Texts",
          applyQuery: { "userBook.tags": { $size: 0 } },
        },
        {
          id: "restoreToArchive",
          name: "Restore to Archive",
          applyQuery: { "userBook.tags": { $size: 0 } },
        },
        { id: "splitter1", splitter: true },
        {
          id: "permanentlyDeleteTexts",
          name: "Permanently Delete Text",
          namePlural: "Permanently Delete Texts",
          destructive: true,
          applyQuery: { "userBook.tags": { $size: 0 } },
        },
        { id: "splitter2", splitter: true },
        {
          id: "addToReadingQueue",
          name: "Queue to Read",
          applyQuery: {
            $and: [
              { "userBook.tags": { $ne: "queue" } },
              { "userBook.tags": { $not: { $size: 0 } } },
            ],
          },
        },
        {
          id: "markAsCompleted",
          name: "Mark as Completed",
          applyQuery: {
            $and: [
              { "userBook.tags": { $ne: "completed" } },
              { "userBook.tags": { $not: { $size: 0 } } },
            ],
          },
        },
        {
          id: "archiveTexts",
          name: "Archive Text",
          namePlural: "Archive Texts",
          applyQuery: {
            $and: [
              { "userBook.tags": { $ne: "abandoned" } },
              { "userBook.tags": { $not: { $size: 0 } } },
            ],
          },
        },
        { id: "splitter3", splitter: true },
        {
          id: "renameText",
          name: "Rename Text",
          singleSelectionOnly: true,
          applyQuery: {
            "ownerID._id": stores.user.id.value,
            "userBook.tags": { $not: { $size: 0 } },
          },
          exec: async () => {
            const itemIDs = this.props.books.selectionStore.itemIDs.value;
            if (itemIDs.length !== 1) return;
            const book = this.props.books.get(itemIDs[0]);
            if (!book) return;
            const currentTitle = book.book.value.title || "";
            const newTitle = prompt("Enter new title:", currentTitle);
            if (
              newTitle !== null &&
              newTitle.trim() !== "" &&
              newTitle !== currentTitle
            ) {
              await book.patchBook({ title: newTitle.trim() });
            }
          },
        },
        { id: "splitter4", splitter: true },
        {
          id: "share",
          name: "Share Publicly",
          namePlural: "Share Publicly",
          applyQuery: {
            public: { $ne: true },
            "ownerID._id": stores.user.id.value,
            "userBook.tags": { $not: { $size: 0 } },
          },
        },
        {
          id: "shareUnlisted",
          name: "Share to Anyone With Link",
          namePlural: "Share to Anyone With Link",
          applyQuery: {
            public: { $ne: true },
            "ownerID._id": stores.user.id.value,
            "userBook.tags": { $not: { $size: 0 } },
          },
        },
        {
          id: "unShare",
          name: "Un-Share Text",
          namePlural: "Un-Share Texts",
          applyQuery: {
            public: true,
            "ownerID._id": stores.user.id.value,
          },
        },
        { id: "splitter5", splitter: true },
        {
          id: "arrangeOnShelves",
          name: "Arrange on Shelves…",
          // Texts in the library (not deleted)
          applyQuery: { "userBook.tags": { $not: { $size: 0 } } },
          exec: () => this.showShelves(),
        },
        {
          id: "removeFromShelf",
          name: "Remove From Shelf",
          applyQuery: { shelf: { $exists: true } },
        },
        { id: "splitter6", splitter: true },
        {
          id: "deleteAudio",
          name: "Delete Audio",
          destructive: true,
          applyQuery: {
            "ownerID._id": stores.user.id.value,
            audioFileID: { $exists: true },
          },
          exec: () => this.props.books.deleteAudioFromSelection(),
        },
        {
          id: "deleteTexts",
          name: "Delete Text",
          namePlural: "Delete Texts",
          destructive: true,
          applyQuery: {
            $and: [{ "userBook.tags": { $not: { $size: 0 } } }],
          },
          exec: () => this.deleteTexts(),
        },
      ],
      { selectionStore: this.props.books.selectionStore }
    );
  }

  shelfMode() {
    return Boolean(this.props.shelfID) && this.props.shelfID !== "unshelved";
  }

  // The Type column only earns its space when some text is audio or video;
  // otherwise it's blank for every row, so we omit it.
  hasContentTypes() {
    return this.props.books.books.value.some((book) => {
      const bookJSON = book.book.value;
      return Boolean(bookJSON.youTubeID || bookJSON.audioFileID);
    });
  }

  setupSortable() {
    this.sortable?.destroy();
    this.sortable = undefined;
    if (!this.tbodyElement) return;
    // Dragging saves the visible row order as the shelf's order, so only allow
    // it in a shelf shown in its default order (no column sort active).
    if (!this.shelfMode() || this.state.sortColumn !== null) return;

    this.sortable = Sortable.create(this.tbodyElement, {
      handle: ".col-dragHandle",
      animation: 150,
      onUpdate: () => {
        const shelf = this.props.shelfID
          ? stores.shelves.get(this.props.shelfID)
          : undefined;
        if (!shelf) return;

        const oldBooksList = getValidBooks(shelf);
        const books = Array.from(
          this.tbodyElement!.querySelectorAll<HTMLTableRowElement>(
            "tr[data-bookID]"
          )
        )
          .map((row) => {
            const bookID = row.getAttribute("data-bookID");
            return oldBooksList.find((book) => book.bookID === bookID);
          })
          .filter(Boolean);

        void stores.shelves.patchShelf(shelf._id, { books });
      },
    });
  }

  shouldShowBook(book: BookStore) {
    if (this.props.shelfID) {
      const shelf = stores.shelves.get(this.props.shelfID);
      return shelf ? Boolean(getBookIndex(shelf)[book.id]) : false;
    }
    return containsTag(book, this.props.tag);
  }

  // The sort applied when no column header is active: shelves keep their manual
  // order (null); non-shelf lists fall back to most-recently-read first, so the
  // Last Read column shows as the implicit default sort.
  defaultSort(): { column: SortColumn; ascending: boolean } | null {
    return this.shelfMode() ? null : { column: "lastRead", ascending: false };
  }

  // The sort currently in effect, including the implicit default.
  effectiveSort(): { column: SortColumn; ascending: boolean } | null {
    if (this.state.sortColumn) {
      return {
        column: this.state.sortColumn,
        ascending: this.state.sortAscending,
      };
    }
    return this.defaultSort();
  }

  // Clicking a header cycles: ascending, descending, then no sort (default
  // order). Last Read is the implicit default sort, so it only toggles between
  // descending and ascending — never back to "no sort".
  toggleSort = (column: SortColumn) => {
    this.setState((previous) => {
      if (column === "lastRead") {
        const ascending =
          previous.sortColumn === "lastRead" ? !previous.sortAscending : true;
        return { sortColumn: "lastRead", sortAscending: ascending };
      }
      if (previous.sortColumn !== column) {
        return { sortColumn: column, sortAscending: true };
      }
      if (previous.sortAscending) {
        return { sortColumn: column, sortAscending: false };
      }
      return { sortColumn: null, sortAscending: true };
    });
  };

  // The mobile sort menu picks a column outright rather than cycling it.
  // Last Read reads best newest-first; every other column starts ascending
  // (A→Z, easiest, least-shared, shortest, least-read). Picking the sorted
  // column again reverses it.
  changeSortColumn = (column: string) => {
    const sort = this.effectiveSort();
    if (column && column === sort?.column) {
      this.setState({
        sortColumn: sort.column,
        sortAscending: !sort.ascending,
      });
      return;
    }
    this.setState({
      sortColumn: (column || null) as SortColumn | null,
      sortAscending: column !== "lastRead",
    });
  };

  // Every sortable header shows the same affordance: the active column gets a
  // solid up/down indicator; the rest get a faint chevron that CSS only reveals
  // on hover, so the columns read as sortable without permanently adding ink.
  sortAffordance(column: SortColumn) {
    const sort = this.effectiveSort();
    if (sort && sort.column === column) {
      return (
        <i
          class={`sortIndicator icon-sort-${sort.ascending ? "up" : "down"}`}
        ></i>
      );
    }
    return <i class="sortHint icon-sort-down"></i>;
  }

  displayedBooks() {
    const sort = this.effectiveSort();
    if (!sort) return this.props.books.books.value;
    return sortBooks(this.props.books.books.value, sort.column, sort.ascending);
  }

  private showShelves() {
    showShelvesSheet(
      this.props.books.selectionStore,
      this.amendSelectionForCurrentFilter
    );
  }

  amendSelectionForCurrentFilter = () => {
    this.applyVisibilityFilter();
    this.forceUpdate();
  };

  // The IDs of the rows the user actually sees, in the order they're rendered.
  // Shift-click range selection walks this list, so it must follow the active
  // column sort (displayedBooks()), not the collection's backing order, and
  // exclude rows hidden by the visibility filter.
  visibleBookIDs() {
    return this.displayedBooks()
      .filter((book) => this.shouldShowBook(book))
      .map((book) => book.id);
  }

  selected(bookID: string) {
    return this.props.books.selectionStore.itemIDs.value.includes(bookID);
  }

  clickCheckbox = (event: MouseEvent) => {
    event.stopPropagation();
    this.checkboxClickShiftKey = event.shiftKey;
  };

  changeCheckbox = (event: Event, bookID: string) => {
    event.stopPropagation();
    const selectionStore = this.props.books.selectionStore;
    const visibleBookIDs = this.visibleBookIDs();
    let checked = (event.currentTarget as HTMLInputElement).checked;
    let range: string[] | undefined;

    if (this.checkboxClickShiftKey && this.state.previousCheckboxID) {
      const index1 = visibleBookIDs.indexOf(bookID);
      const index2 = visibleBookIDs.indexOf(this.state.previousCheckboxID);
      if (index1 !== -1 && index2 !== -1) {
        range = visibleBookIDs.slice(
          Math.min(index1, index2),
          Math.max(index1, index2) + 1
        );
        checked = Boolean(this.state.previousCheckboxChecked);
      }
    }

    if (checked) {
      if (range) {
        selectionStore.addItems(range);
      } else {
        selectionStore.addItem(bookID);
      }
    } else if (range) {
      selectionStore.wholeCollection.value = false;
      selectionStore.wholePage.value = false;
      selectionStore.removeItems(range);
    } else {
      selectionStore.removeItem(bookID);
    }

    this.setState({
      previousCheckboxChecked: checked,
      previousCheckboxID: bookID,
    });
    this.checkboxClickShiftKey = false;
  };

  async deleteTexts() {
    const itemIDs = this.props.books.selectionStore.itemIDs.value;
    const selectedBooks = this.props.books.selectionStore
      .getSelectedItems()
      .filter((book): book is BookStore => Boolean(book));
    const shared = selectedBooks.filter((book) =>
      (book.book.value.userBook?.tags ?? []).includes("shared")
    );

    if (shared.length > 0) {
      alert(
        "Can't delete since the following texts are shared publicly: \n\n- " +
          shared.map((book) => book.book.value.title).join("\n- ") +
          "\n\nUn-share these texts first before deleting them."
      );
      return;
    }

    const shelves = stores.shelves.shelvesContainingBooks(itemIDs);

    if (shelves.length > 0) {
      const confirmed = await new Promise<boolean>((resolve) => {
        const shelfNames = shelves.map((shelf) => shelf.name);
        genericModals.confirm({
          title: "Delete Text" + (itemIDs.length === 1 ? "" : "s"),
          warning: true,
          message:
            "Are you sure you want to delete " +
            (itemIDs.length === 1
              ? "this text? It"
              : "these " + itemIDs.length + " texts? They") +
            " will be removed from the following " +
            (stores.shelves.shelves.value.length === 1 ? "shelf" : "shelves") +
            ": \n\n- " +
            shelfNames.join("\n- "),
          callback: (response: string) => {
            resolve(response === "ok");
          },
        });
      });

      if (!confirmed) return;
    }

    const unshelved = stores.shelves.unshelved;
    if (unshelved) {
      const books = getValidBooks(unshelved).filter(
        (book) => !itemIDs.includes(book.bookID)
      );
      stores.shelves.updateShelfBooks("unshelved", books);
    }

    for (const shelf of shelves) {
      const books = getValidBooks(shelf).filter(
        (book) => !itemIDs.includes(book.bookID)
      );
      await stores.shelves.patchShelf(shelf._id, { books });
    }

    await this.props.books.deleteTexts();
  }

  // On mobile the type / difficulty / length / privacy / owner / status columns are
  // hidden and their values collapse into this muted line under the title,
  // led by the category chip (desktop hides the line via CSS instead). Values
  // that would repeat on almost every row stay off it: the "Text" type, the
  // private lock, and the user's own username.
  renderMobileMetaLine(bookJSON: any, category: string | null) {
    const parts: preact.ComponentChildren[] = [];
    const type = bookContentType(bookJSON);
    if (type !== "text") {
      parts.push(
        <span title={contentTypeLabel(type)}>
          <i class={contentTypeIcon(type)}></i>
        </span>
      );
    }
    if (bookJSON.metrics?.CEF) {
      parts.push(
        <span title={bookJSON.metrics.difficultyString}>
          {bookJSON.metrics.CEF}
        </span>
      );
    }
    if (bookJSON.totalWordsString) {
      parts.push(
        <span>
          {bookJSON.totalWordsString}{" "}
          {languageMap.usesCharacterCount(bookJSON.language)
            ? "characters"
            : "words"}
        </span>
      );
    }
    if (bookJSON.public) {
      parts.push(
        <i
          title={permissionTitle(bookJSON)}
          class={`permission ${permissionIcon(bookJSON)}`}
        ></i>
      );
    }
    if (
      bookJSON.ownerID?._id &&
      bookJSON.ownerID._id !== stores.user.id.value
    ) {
      parts.push(
        <a
          href={userProfilePath(
            bookJSON.ownerID.username,
            bookJSON.ownerID._id
          )}
          title={bookJSON.ownerID.displayName}
        >
          {bookJSON.ownerID.shortDisplayName}
        </a>
      );
    }
    const status = readStatus(bookJSON);
    // A bar has a line of its own, below
    const showsBar = status?.percent !== undefined;
    if (status && !showsBar) parts.push(renderReadStatus(status));
    return (
      <div class="mobileMetaLine">
        {renderCategoryChip(category)}
        {parts.map((part, index) => (
          <preact.Fragment key={index}>
            {index > 0 ? <span class="metaDot">·</span> : null}
            {part}
          </preact.Fragment>
        ))}
        {status && showsBar ? renderReadStatus(status) : null}
      </div>
    );
  }

  // The hidden columns take their tap-to-sort headers with them on mobile, so
  // the sort moves into a menu beside the actions. Desktop hides it via CSS and
  // keeps the column headers.
  renderMobileSortMenu(showContentType: boolean) {
    const options = sortOptions.filter(
      (option) => option.value !== "contentType" || showContentType
    );
    return (
      <SortAndFilterMenu
        sortOptions={
          this.shelfMode()
            ? [{ value: "", label: "Shelf order" }, ...options]
            : options
        }
        sortBy={this.effectiveSort()?.column ?? ""}
        sortAscending={this.effectiveSort()?.ascending}
        onSort={this.changeSortColumn}
      />
    );
  }

  renderBookRow(book: BookStore, rowIndex: number, showContentType: boolean) {
    const bookJSON = book.viewJSON(stores.user.id.value, false) as any;
    bookJSON.createdDate = dateFromObjectId(book.id);
    if (bookJSON.userBook) {
      bookJSON.userBook.positionString = utils.addCommasToNumber(
        bookJSON.userBook.position
      );
    }
    const lengthValue = languageMap.usesCharacterCount(bookJSON.language)
      ? bookJSON.metrics?.characters
      : bookJSON.metrics?.words;
    if (typeof lengthValue === "number") {
      bookJSON.totalWordsString = utils.addCommasToNumber(
        parseFloat(String(lengthValue))
      );
    }
    if (bookJSON.ownerID?.username) {
      bookJSON.ownerID.displayName =
        bookJSON.ownerID.displayName || bookJSON.ownerID.username;
      bookJSON.ownerID.shortDisplayName = utils.elide(
        bookJSON.ownerID.displayName,
        12
      );
    }

    const selected = this.selected(book.id);
    const shelf = this.props.shelfID
      ? stores.shelves.get(this.props.shelfID)
      : undefined;
    const rowClasses = ["subtleContainer"];
    if (selected) rowClasses.push("selected");
    if (!this.shouldShowBook(book)) rowClasses.push("filteredOut");
    if (
      bookJSON.status === "notAvailable" ||
      (shelf && shelf.public && !bookJSON.public)
    ) {
      rowClasses.push("notAvailable");
    }

    const category = categoryOf(bookJSON);
    if (category) rowClasses.push(`category-${category}`);
    const status = readStatus(bookJSON);

    const checkboxID = `checkbox-${book.id}-${rowIndex}`;

    return (
      <tr
        class={rowClasses.join(" ")}
        data-bookID={book.id}
        key={`${book.id}-${rowIndex}`}
      >
        <td class="col-dragHandle">
          <i class="icon-align-justify"></i>
        </td>
        <td class="col-checkbox">
          <div class="checkboxCell clickable">
            <input
              key={`${checkboxID}-${selected ? "checked" : "unchecked"}`}
              checked={selected}
              data-bookID={book.id}
              id={checkboxID}
              onClick={this.clickCheckbox}
              onInput={(event) => this.changeCheckbox(event, book.id)}
              type="checkbox"
            />
            <label class="checkboxLabel" for={checkboxID}>
              <i class="icon-check"></i>
              <i class="icon-check-empty"></i>
              <i class="icon-check-minus"></i>
            </label>
          </div>
        </td>
        <td class="col-title">
          <a href={`/library/${book.id}`}>{bookJSON.shortTitle}</a>
          {renderCategoryChip(category)}
          <div>
            <small>
              <span class="description">{bookJSON.description}</span>
            </small>
          </div>
          {this.renderMobileMetaLine(bookJSON, category)}
        </td>
        {showContentType ? (
          <td class="col-contentType">
            {(() => {
              const type = bookContentType(bookJSON);
              const label = contentTypeLabel(type);
              return (
                <span title={label}>
                  <i class={contentTypeIcon(type)}></i>
                  <span class="contentTypeLabel"> {label}</span>
                </span>
              );
            })()}
          </td>
        ) : null}
        <td class="col-difficulty">
          <span style="color:#777" title={bookJSON.metrics?.difficultyString}>
            {bookJSON.metrics?.CEF}
          </span>
        </td>
        <td class="col-permissions">
          <i
            title={permissionTitle(bookJSON)}
            class={`permission ${permissionIcon(bookJSON)}`}
          ></i>
        </td>
        <td class="col-owner">
          {bookJSON.ownerID?._id ? (
            <a
              href={userProfilePath(
                bookJSON.ownerID.username,
                bookJSON.ownerID._id
              )}
              title={bookJSON.ownerID.displayName}
            >
              {bookJSON.ownerID.shortDisplayName}
            </a>
          ) : null}
        </td>
        <td class="col-wordCount">{bookJSON.totalWordsString}</td>
        <td class="col-readStatus">
          {status ? renderReadStatus(status) : null}
        </td>
        <td class="col-lastRead">
          {formatLastRead(bookJSON.userBook?.lastReadDate)}
        </td>
      </tr>
    );
  }

  render() {
    const shelfMode = this.shelfMode();
    // Drag-reorder (and its handles) only when a shelf is shown in its own order
    // (no column sort active).
    const draggable = shelfMode && this.state.sortColumn === null;
    const showContentType = this.hasContentTypes();
    const small = deviceDetect.smallViewport.value;
    const selectionStore = this.props.books.selectionStore;

    return (
      <>
        <div class="listControls">
          <div class="checkboxCell selectAllContainer">
            <SelectAllView model={this.props.books.selectionStore} />
          </div>
          <SelectionActionView
            itemIDsOnly
            model={selectionStore}
            selectionActionList={this.createSelectionActionList()}
          />
          {this.props.searchControls}
          {this.props.addButtons}
          {this.renderMobileSortMenu(showContentType)}
        </div>
        <table class={`lined table texts${draggable ? " draggable" : ""}`}>
          <thead>
            <tr>
              <th class="col-dragHandle"></th>
              <th class="col-checkbox"></th>
              <th
                class="col-title sortable"
                onClick={() => this.toggleSort("title")}
              >
                Title {this.sortAffordance("title")}
              </th>
              {showContentType ? (
                <th
                  class="col-contentType sortable"
                  onClick={() => this.toggleSort("contentType")}
                  title="Sort by type"
                >
                  {this.sortAffordance("contentType")}
                </th>
              ) : null}
              <th
                class="col-difficulty sortable"
                onClick={() => this.toggleSort("difficulty")}
                title="Sort by difficulty"
              >
                {this.sortAffordance("difficulty")}
              </th>
              <th
                class="col-permissions sortable"
                onClick={() => this.toggleSort("permissions")}
                title="Sort by share status"
              >
                {this.sortAffordance("permissions")}
              </th>
              <th
                class="col-owner sortable"
                onClick={() => this.toggleSort("owner")}
              >
                Owner {this.sortAffordance("owner")}
              </th>
              <th
                class="col-wordCount sortable"
                onClick={() => this.toggleSort("length")}
              >
                Length {this.sortAffordance("length")}
              </th>
              <th
                class="col-readStatus sortable"
                onClick={() => this.toggleSort("readStatus")}
              >
                Progress {this.sortAffordance("readStatus")}
              </th>
              <th
                class="col-lastRead sortable"
                onClick={() => this.toggleSort("lastRead")}
              >
                Last Read {this.sortAffordance("lastRead")}
              </th>
            </tr>
          </thead>
          <tbody
            ref={(element) => {
              this.tbodyElement = element;
            }}
          >
            {this.props.books.visibleBooks().length === 0 ? (
              <tr>
                <td colSpan={42} style="text-align:center">
                  No texts found
                </td>
              </tr>
            ) : null}
            {this.displayedBooks().map((book, index) =>
              this.renderBookRow(book, index, showContentType)
            )}
          </tbody>
        </table>
      </>
    );
  }
}
